import { LayoutGrid, ArrowUpRight, Lock, Sparkles } from "lucide-react";
import { useHive } from "@/store/useHive";
import { APPS } from "@/data/apps";
import { productState } from "@/lib/entitlements";
import type { ProductId } from "@/api/types";

/** Launch order for the product cards (filtered by entitlement below). */
const PRODUCT_ORDER: ProductId[] = ["ares", "garlic", "athena", "byonc", "appgraph"];

/**
 * Desktop centerpiece — one card per Momentum product the tenant can see.
 * States come from the session's entitlement payload: `hidden` products are
 * not rendered, `preview`/`locked` keep full branding with an Early-Access
 * treatment, and clicking never dead-ends (openApp routes to the preview
 * window or modal instead of the real app).
 */
export function AppLaunchpad() {
  const openApp = useHive((s) => s.openApp);
  const entitlements = useHive((s) => s.entitlements);

  const products = PRODUCT_ORDER.filter((id) => productState(entitlements, id) !== "hidden");
  if (products.length === 0) return null;

  return (
    <div className="w-[570px]">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs font-medium text-app-2">
          <LayoutGrid size={14} className="accent" /> Select an app
        </div>
        <span className="text-[10px] uppercase tracking-[0.18em] text-app-2">
          Signed in via Hive
        </span>
      </div>

      <div
        className="mt-3 grid gap-2.5"
        style={{ gridTemplateColumns: `repeat(${products.length}, minmax(0, 1fr))` }}
      >
        {products.map((id) => {
          const a = APPS[id];
          const state = productState(entitlements, id);
          const gated = state === "preview" || state === "locked";
          return (
            <button
              key={id}
              onClick={() => openApp(id)}
              className="group relative flex flex-col items-start gap-2.5 rounded-xl border p-3 text-left outline-none transition focus-visible:ring-2 focus-visible:ring-[hsl(var(--accent)/0.5)]"
              style={{
                borderColor: `${a.accent}3d`,
                background: `linear-gradient(180deg, ${a.accent}17, transparent 70%)`,
              }}
              aria-label={gated ? `Preview ${a.name} (early access)` : `Open ${a.name}`}
            >
              {/* Accent halo — appears on hover without needing dynamic Tailwind classes */}
              <span
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 rounded-xl opacity-0 transition duration-200 group-hover:opacity-100"
                style={{
                  boxShadow: `inset 0 0 0 1px ${a.accent}90, 0 14px 36px -14px ${a.accent}73`,
                }}
              />
              {!gated && (
                <ArrowUpRight
                  size={13}
                  aria-hidden="true"
                  className="absolute right-2.5 top-2.5 text-app-2 opacity-0 transition duration-200 group-hover:opacity-100"
                />
              )}
              {state === "locked" && (
                <Lock size={12} className="absolute right-2.5 top-2.5" style={{ color: a.accent }} />
              )}
              <div
                className="grid h-10 w-10 place-items-center rounded-xl text-white shadow-lg"
                style={{ background: `linear-gradient(135deg, ${a.accent}, ${a.accent}aa)` }}
              >
                <a.icon size={18} />
              </div>
              <div className="min-w-0">
                <div className="text-[13px] font-semibold leading-tight text-app">{a.name}</div>
                <p className="mt-1 text-[11px] leading-snug text-app-2">{a.tagline}</p>
                {gated && (
                  <span
                    className="mt-1.5 inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide"
                    style={{ background: `${a.accent}22`, color: a.accent }}
                  >
                    <Sparkles size={8} /> Early Access
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
